import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, X, Clock, CheckCircle, XCircle, FileText, Buildings } from "@phosphor-icons/react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUS_STYLES = {
  approved: { icon: CheckCircle, color: "#2563EB", bg: "bg-blue-50", border: "border-blue-200" },
  pending:  { icon: Clock,       color: "#A16207", bg: "bg-amber-50", border: "border-amber-200" },
  rejected: { icon: XCircle,     color: "#DC2626", bg: "bg-red-50",   border: "border-red-200" },
  draft:    { icon: FileText,    color: "#52525B", bg: "bg-zinc-50",  border: "border-zinc-200" },
};

const EMPTY_FORM = {
  title: "",
  description: "",
  topic: "General",
  type: "academic",
  location_name: "",
  city: "",
  country: "",
  lat: 0,
  lng: 0,
  start_date: "",
  end_date: "",
  website: "",
  image_url: "",
  cfp_deadline: "",
  cfp_url: "",
  host_organization: "",
  tags: [],
  issn: [],
};

export default function OrganizerPage() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);  // null = panel closed; object = open

  async function refresh() {
    if (!user || !user.role) return;
    const { data } = await api.get("/organizer/my-conferences");
    setMine(data || []);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user]);

  function newConference() { setForm({ ...EMPTY_FORM }); }
  function edit(c) { setForm({ ...EMPTY_FORM, ...c }); }
  function cancel() { setForm(null); }

  async function submit() {
    if (!form.title || !form.start_date || !form.end_date || !form.city || !form.country) {
      toast.error(t("organizer.fillRequired")); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : (form.tags || []),
        issn: typeof form.issn === "string" ? form.issn.split(",").map((s) => s.trim()).filter(Boolean) : (form.issn || []),
        lat: Number(form.lat) || 0,
        lng: Number(form.lng) || 0,
      };
      delete payload.created_at;
      delete payload.submitted_by;
      delete payload._id;
      if (form.id) {
        await api.put(`/organizer/conferences/${form.id}`, payload);
        toast.success(t("organizer.updated"));
      } else {
        await api.post(`/organizer/submit`, payload);
        toast.success(t("organizer.submitted"));
      }
      setForm(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail ? JSON.stringify(e.response.data.detail) : t("organizer.saveFailed"));
    } finally { setBusy(false); }
  }

  if (loading) return <div className="px-6 md:px-10 lg:px-16 py-16">{t("common.loading")}</div>;
  if (!user || !user.role) {
    return (
      <div data-testid="organizer-page" className="px-6 md:px-10 lg:px-16 py-16 max-w-md mx-auto text-center">
        <h1 className="font-display font-bold text-4xl mb-3">{t("common.pleaseSignIn")}</h1>
        <Link to="/login" className="btn-primary">{t("common.signIn")}</Link>
      </div>
    );
  }

  return (
    <div data-testid="organizer-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16 max-w-6xl">
      <div className="overline mb-3">{t("organizer.breadcrumb")}</div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-[-0.04em] leading-[.92]">
            {t("organizer.title")}
          </h1>
          <p className="text-zinc-600 max-w-2xl text-base mt-3 leading-relaxed">{t("organizer.intro")}</p>
        </div>
        {!form && (
          <button onClick={newConference} data-testid="organizer-new-btn" className="btn-primary"><Plus size={14} /> {t("organizer.submitNew")}</button>
        )}
      </div>

      {form && (
        <SubmitForm form={form} setForm={setForm} onCancel={cancel} onSubmit={submit} busy={busy} t={t} />
      )}

      <div className="mt-14">
        <div className="flex items-center justify-between border-b border-zinc-950 pb-3 mb-6">
          <h2 className="font-display font-bold text-2xl tracking-tight flex items-center gap-2"><Buildings size={20} weight="duotone" /> {t("organizer.mySubmissions")}</h2>
          <span className="font-mono text-xs text-zinc-400">{mine.length}</span>
        </div>
        {mine.length === 0 ? (
          <div className="border border-zinc-200 p-12 text-center text-zinc-500" data-testid="organizer-empty">{t("organizer.emptyState")}</div>
        ) : (
          <ul className="divide-y divide-zinc-200 border border-zinc-200">
            {mine.map((c) => {
              const st = STATUS_STYLES[c.status || "approved"] || STATUS_STYLES.approved;
              const Icon = st.icon;
              return (
                <li key={c.id} data-testid={`organizer-row-${c.id}`} className="px-5 py-4 hover:bg-[#FAFAFA] flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${st.border} ${st.bg}`} style={{ color: st.color }}>
                        <Icon size={11} weight="fill" /> {t(`organizer.status_${c.status || "approved"}`)}
                      </span>
                      <span className="tag">{c.topic}</span>
                    </div>
                    <Link to={`/conferences/${c.id}`} className="font-display font-semibold text-base tracking-tight leading-tight block mt-2 hover:text-blue-700">{c.title}</Link>
                    <div className="text-xs text-zinc-500 mt-1 font-mono">
                      {c.start_date && format(new Date(c.start_date), "MMM d, yyyy")} · {c.city}{c.country ? `, ${c.country}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => edit(c)} data-testid={`organizer-edit-${c.id}`} className="btn-secondary text-xs" style={{ padding: ".45rem .75rem" }}><Pencil size={12} /> {t("common.edit")}</button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function SubmitForm({ form, setForm, onCancel, onSubmit, busy, t }) {
  function setF(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  const isEdit = !!form.id;
  const fields = [
    ["title",            { type: "text", required: true, full: true }],
    ["description",      { type: "textarea", full: true }],
    ["topic",            { type: "text", required: true }],
    ["type",             { type: "select", options: ["academic", "tech", "both"] }],
    ["host_organization",{ type: "text" }],
    ["website",          { type: "text" }],
    ["location_name",    { type: "text", required: true }],
    ["city",             { type: "text", required: true }],
    ["country",          { type: "text", required: true }],
    ["start_date",       { type: "date", required: true }],
    ["end_date",         { type: "date", required: true }],
    ["cfp_deadline",     { type: "date" }],
    ["cfp_url",          { type: "text" }],
    ["image_url",        { type: "text" }],
    ["tags",             { type: "csv", full: true }],
  ];

  return (
    <div className="mt-10 border-2 border-zinc-950 bg-white p-6" data-testid="organizer-form">
      <div className="flex items-center justify-between mb-5 border-b border-zinc-200 pb-3">
        <div className="overline">{isEdit ? t("organizer.editing") : t("organizer.newConference")}</div>
        <button onClick={onCancel} data-testid="organizer-form-close" className="text-zinc-500 hover:text-zinc-950"><X size={18} /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
        {fields.map(([k, opts]) => (
          <div key={k} className={opts.full ? "sm:col-span-2" : ""}>
            <label className="overline block mb-1">{k.replace(/_/g, " ")}{opts.required ? " *" : ""}</label>
            {opts.type === "textarea" ? (
              <textarea data-testid={`org-form-${k}`} value={form[k] || ""} onChange={(e) => setF(k, e.target.value)} className="input-box min-h-[100px]" />
            ) : opts.type === "select" ? (
              <select data-testid={`org-form-${k}`} value={form[k] || ""} onChange={(e) => setF(k, e.target.value)} className="input-box">
                {opts.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : opts.type === "csv" ? (
              <input data-testid={`org-form-${k}`} value={Array.isArray(form[k]) ? form[k].join(", ") : (form[k] || "")} onChange={(e) => setF(k, e.target.value)} className="input-box" placeholder="comma, separated" />
            ) : (
              <input data-testid={`org-form-${k}`} type={opts.type} value={form[k] || ""} onChange={(e) => setF(k, e.target.value)} className="input-box" />
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 flex justify-end gap-3 border-t border-zinc-200 pt-4">
        <button onClick={onCancel} data-testid="organizer-form-cancel" className="btn-secondary">{t("common.cancel")}</button>
        <button onClick={onSubmit} disabled={busy} data-testid="organizer-form-submit" className="btn-primary">{busy ? "…" : (isEdit ? t("common.save") : t("organizer.submitForReview"))}</button>
      </div>
      {!isEdit && (
        <div className="mt-3 text-xs font-mono text-zinc-500">{t("organizer.reviewDisclaimer")}</div>
      )}
    </div>
  );
}
